import {
  Button,
  CircularProgress,
  Grid,
  Snackbar,
  TextField,
} from "@material-ui/core";
import {
  placeholderDescripcionAdicional,
  placeholderMarcas,
} from "../constants/form-constants";
import { IconCircleX, IconPlus, IconRefresh } from "@tabler/icons-react";
import { BLDetailsValues } from "../../../interfaces/bl-export/request.bl.interface";
import { useFormik } from "formik";
import { BlDetailSchema } from "../constants/form.shemas";
import { useTextfieldStyles } from "../../../theme/textfieldStyles";
import { useGetContainerByCodeContainerMutation } from "../../../services/bl-export/BLExportService";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { setContainerToUpdate, setContainers } from "../slices/BlExportSlice";
import { Alert, Autocomplete } from "@material-ui/lab";
import { useGetCatalogosQuery } from "../../../services/catalogs/catalogsService";

interface props {
  onCancel?: () => void;
  containers: BLDetailsValues[];
  isEditParentBL?: boolean;
}

const NewContainerForm = ({ onCancel, containers, isEditParentBL }: props) => {
  const classes = useTextfieldStyles();
  const dispatch = useAppDispatch();
  const { containerToUpdate, isFormUpdateContainer } = useAppSelector(
    (state) => state.blExport
  );
  const [showSuccess, setShowSucces] = useState(false);

  const handleOpenSucces = () => {
    setShowSucces(true);
  };

  const handleCloseSuccess = () => {
    setShowSucces(false);
  };

  // Queries
  const [
    searchContainerMutation,
    { isLoading: isLoadingSearchContainer, data: dataSearchContainer },
  ] = useGetContainerByCodeContainerMutation();

  const { data: empaques, isLoading: isLoadingEmpaques } = useGetCatalogosQuery(
    {
      page: 0,
      size: 500,
      type: "EMPAQUE",
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const optionsTipoEmpaque = empaques?.data?.map((el) => el.valor) || [];

  const {
    values,
    touched,
    getFieldProps,
    handleSubmit,
    errors,
    resetForm,
    setValues,
    setFieldValue,
  } = useFormik<BLDetailsValues>({
    initialValues: {
      tipo_empaque: "Packages",
      cod_contenedor: "",
      commodities: "",
      desc_ad_cont: "",
      measurement: "0.00",
      num_bultos: "0",
      num_paquetes: "1",
      peso_bruto: "1.00",
      peso_neto: "0.00",
      ref_adicionales: "",
      sellos: "",
    },
    validationSchema: BlDetailSchema,
    onSubmit: (values) => {
      if (dataSearchContainer !== undefined || checkIfNewContainerExists()) {
        return;
      }

      const formattedValues = formatingFloatingsValues(values);

      // Update container selected into array containers
      if (isFormUpdateContainer === true && containerToUpdate !== undefined) {
        dispatch(setContainers(updateContainerInArray(formattedValues)));
        dispatch(setContainerToUpdate(undefined));
        resetForm();
        handleOpenSucces();
        return;
      }

      // Add new container
      dispatch(setContainers([...containers, formattedValues]));
      dispatch(setContainerToUpdate(undefined));
      resetForm();
      handleOpenSucces();
    },
  });

  const updateContainerInArray = (values: BLDetailsValues) => {
    let containersUpdated = [...containers];
    const indexToUpdate = containersUpdated.findIndex(
      (ct) => ct.cod_contenedor === containerToUpdate!.cod_contenedor
    );
    containersUpdated[indexToUpdate] = values;
    return containersUpdated;
  };

  const checkIfNewContainerExists = () => {
    if (isFormUpdateContainer === true) {
      return Boolean(
        containers
          .filter(
            (ct) => ct.cod_contenedor !== containerToUpdate?.cod_contenedor
          )
          .find((ct) => ct.cod_contenedor === values.cod_contenedor)
      );
    }
    return Boolean(
      containers.find(
        (container) => container.cod_contenedor === values.cod_contenedor
      )
    );
  };

  /**
   * Retorna valores sin coma (Delimitador de miles)
   * @param values
   * @returns
   */
  const formatingFloatingsValues = (values: BLDetailsValues) => {
    let formattedValues = { ...values };

    formattedValues.peso_bruto = values.peso_bruto.replaceAll(",", "");
    formattedValues.peso_neto = values.peso_neto.replaceAll(",", "");
    formattedValues.measurement = values.measurement.replaceAll(",", "");

    return formattedValues;
  };

  const _submit = () => {
    handleSubmit();
  };

  const _onCancel = () => {
    resetForm();
    if (onCancel) {
      onCancel();
    }
  };

  const validateClassname = (keyName: string) => {
    const _touched: any = { ...touched };
    if (
      !(keyName in errors) &&
      _touched[`${keyName}`] &&
      _touched[`${keyName}`] === true
    ) {
      return classes.root;
    }
    return "";
  };

  useEffect(() => {
    if (values.id_det_bl !== undefined || isFormUpdateContainer === true) {
      return;
    }
    searchContainerMutation({ cod_container: values.cod_contenedor });
  }, [
    values.cod_contenedor,
    searchContainerMutation,
    values.id_det_bl,
    isFormUpdateContainer,
  ]);

  useEffect(() => {
    if (containerToUpdate !== undefined) {
      setValues(containerToUpdate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerToUpdate]);

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Autocomplete
            loading={isLoadingEmpaques}
            options={optionsTipoEmpaque}
            value={values.tipo_empaque}
            disableClearable={true}
            onChange={(_, val) => setFieldValue("tipo_empaque", val)}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Tipo de Embalaje"
                error={Boolean(errors.tipo_empaque && touched.tipo_empaque)}
                helperText={
                  Boolean(errors.tipo_empaque && touched.tipo_empaque) &&
                  errors.tipo_empaque
                }
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            className={
              Boolean(dataSearchContainer !== undefined) ||
              checkIfNewContainerExists()
                ? ""
                : validateClassname("cod_contenedor")
            }
            {...getFieldProps("cod_contenedor")}
            value={String(values.cod_contenedor).toUpperCase()}
            onChange={(val) =>
              setFieldValue("cod_contenedor", val.target.value.toUpperCase())
            }
            variant="outlined"
            fullWidth
            label="Nº Contenedor*"
            placeholder="ABCD1234567"
            error={
              Boolean(errors.cod_contenedor && touched.cod_contenedor) ||
              checkIfNewContainerExists() ||
              Boolean(dataSearchContainer !== undefined)
            }
            helperText={
              Boolean(dataSearchContainer !== undefined) ||
              checkIfNewContainerExists()
                ? "El número de contenedor ya existe"
                : "Formato 4 letras y 7 números"
            }
            InputProps={{
              endAdornment: isLoadingSearchContainer ? (
                <CircularProgress size={20} />
              ) : null,
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            className={validateClassname("sellos")}
            {...getFieldProps("sellos")}
            variant="outlined"
            fullWidth
            label="Sellos*"
            error={Boolean(errors.sellos && touched.sellos)}
            helperText="Coloque los sellos separados por punto y coma"
          />
        </Grid>
        <Grid item xs={12} md={12}>
          <TextField
            {...getFieldProps("ref_adicionales")}
            variant="outlined"
            fullWidth
            label="Marcas (Opcional)"
            placeholder={placeholderMarcas}
            multiline
            minRows={2}
          />
        </Grid>
        <Grid item xs={12} md={12}>
          <TextField
            className={validateClassname("desc_ad_cont")}
            {...getFieldProps("desc_ad_cont")}
            error={Boolean(errors.desc_ad_cont && touched.desc_ad_cont)}
            helperText={errors.desc_ad_cont}
            variant="outlined"
            label="Descripción Adicional del Contenedor"
            fullWidth
            placeholder={placeholderDescripcionAdicional}
            multiline
            minRows={4}
          />
        </Grid>
        <Grid item xs={12} md={12}>
          <u>
            <h5>Total por Contenedor</h5>
          </u>
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            className={validateClassname("num_paquetes")}
            {...getFieldProps("num_paquetes")}
            variant="outlined"
            fullWidth
            label="Nº Paquetes*"
            error={Boolean(errors.num_paquetes && touched.num_paquetes)}
            helperText={
              "Número entero, separador de miles puede ser coma o punto"
            }
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            className={validateClassname("peso_neto")}
            {...getFieldProps("peso_neto")}
            variant="outlined"
            fullWidth
            label="Peso Neto"
            error={Boolean(errors.peso_neto && touched.peso_neto)}
            helperText={"Número con dos dígitos decimales: Ej: 1,234.56"}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            className={validateClassname("peso_bruto")}
            {...getFieldProps("peso_bruto")}
            variant="outlined"
            fullWidth
            label="Peso Bruto*"
            error={Boolean(errors.peso_bruto && touched.peso_bruto)}
            helperText="Número con dos dígitos decimales: Ej: 1,234.56"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            className={validateClassname("measurement")}
            {...getFieldProps("measurement")}
            variant="outlined"
            fullWidth
            label="Measurment"
            error={Boolean(errors.measurement && touched.measurement)}
            helperText="Número con dos dígitos decimales: Ej: 1,234.56"
          />
        </Grid>
        <Grid item xs={12} />
        <Grid item xs={6}>
          <Button
            startIcon={isFormUpdateContainer ? <IconRefresh /> : <IconPlus />}
            fullWidth
            variant="outlined"
            onClick={_submit}
          >
            {isFormUpdateContainer
              ? "Actualizar Contenedor"
              : " Añadir Contenedor"}
          </Button>
        </Grid>
        <Grid item xs={6}>
          <Button
            startIcon={<IconCircleX />}
            variant="outlined"
            color="secondary"
            fullWidth
            onClick={_onCancel}
          >
            Cancelar
          </Button>
        </Grid>
      </Grid>
      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={handleCloseSuccess}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        style={{ position: "absolute" }}
      >
        <Alert severity="success" variant="filled">
          {isFormUpdateContainer
            ? "Contenedor actualizado correctamente."
            : "Contenedor creado correctamente."}
        </Alert>
      </Snackbar>
    </>
  );
};

export default NewContainerForm;
